/*
 * Theme wishlist (independent of the Ønskeskyen app).
 * Stored per device in localStorage as [{ h: handle, p: price in cents when added, t: timestamp }].
 * - [data-wishlist-toggle][data-handle] buttons toggle a product.
 * - [data-wishlist-count] elements show the count.
 * - window.BABWishlist exposes get/has/add/remove/toggle/subscribe for the wishlist page.
 * Fires GA4-style dataLayer events: add_to_wishlist / remove_from_wishlist.
 */
(function () {
  if (window.BABWishlist) return;

  const KEY = 'bab_wishlist';
  const MAX = 100;
  const config = window.BABWishlistConfig || {};
  // Per-market texts from the header section (window.BABWishlistTexts) win over theme settings.
  const texts = Object.assign({}, config.texts || {});
  const applyTextOverrides = () => {
    const o = window.BABWishlistTexts || {};
    Object.keys(o).forEach((k) => {
      if (o[k]) texts[k] = o[k];
    });
  };
  applyTextOverrides();
  const listeners = new Set();

  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((i) => i && typeof i.h === 'string') : [];
    } catch (e) {
      return [];
    }
  };
  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch (e) {}
    emit();
  };

  const emit = () => {
    applyTextOverrides();
    const list = read();
    syncButtons(list);
    syncCounters(list);
    listeners.forEach((fn) => {
      try {
        fn(list);
      } catch (e) {}
    });
  };

  const push = (event, data) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, data || {}));
  };

  const api = {
    get: read,
    has: (handle) => read().some((i) => i.h === handle),
    add(handle, meta) {
      if (!handle || api.has(handle)) return;
      const list = read();
      list.unshift({ h: handle, p: meta && meta.price ? Number(meta.price) : null, t: Date.now() });
      write(list);
      push('add_to_wishlist', { wishlist_source: 'theme', product_handle: handle, product_title: (meta && meta.title) || '' });
    },
    remove(handle) {
      const list = read();
      const next = list.filter((i) => i.h !== handle);
      if (next.length === list.length) return;
      write(next);
      push('remove_from_wishlist', { wishlist_source: 'theme', product_handle: handle });
    },
    toggle(handle, meta) {
      if (api.has(handle)) {
        api.remove(handle);
        return false;
      }
      api.add(handle, meta);
      return true;
    },
    replace(list) {
      write(list);
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    pageUrl: config.pageUrl || '/pages/onskeliste',
    moneyFormat: config.moneyFormat || '{{amount_with_comma_separator}} kr',
    texts: texts,
  };

  /* ---------- UI sync ---------- */
  function syncButtons(list) {
    const handles = new Set(list.map((i) => i.h));
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const active = handles.has(btn.dataset.handle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = active ? texts.remove : texts.add;
      if (label) {
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);
      }
    });
  }

  function syncCounters(list) {
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = list.length > 99 ? '99+' : String(list.length);
      el.hidden = list.length === 0;
    });
  }

  /* ---------- Toast ---------- */
  let toast;
  let toastTimer;
  function showToast(added) {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'wl-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    const count = read().length;
    toast.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M12 20.3s-7.8-4.7-7.8-10.5A4.5 4.5 0 0 1 12 7.1a4.5 4.5 0 0 1 7.8 2.7c0 5.8-7.8 10.5-7.8 10.5z"/></svg>';
    const span = document.createElement('span');
    span.textContent = added ? texts.added || '' : texts.removed || '';
    toast.appendChild(span);
    if (count > 0 && !isWishlistPage()) {
      const a = document.createElement('a');
      a.href = api.pageUrl;
      a.textContent = (texts.link || '') + ' (' + count + ')';
      toast.appendChild(a);
    }
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3500);
  }

  function isWishlistPage() {
    return !!document.querySelector('[data-wishlist-page]');
  }

  /* ---------- Events ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    // Hearts sit on top of card links — never follow the link.
    e.preventDefault();
    e.stopPropagation();
    const added = api.toggle(btn.dataset.handle, { price: btn.dataset.price, title: btn.dataset.title });
    btn.classList.remove('is-popping');
    if (added) {
      void btn.offsetWidth;
      btn.classList.add('is-popping');
    }
    showToast(added);
  });

  // Other tabs
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) emit();
  });

  // Cards rendered later (filters, infinite scroll, recommendations, quick view)
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      const list = read();
      syncButtons(list);
      syncCounters(list);
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.BABWishlist = api;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', emit);
  else emit();
})();
